// Lecture and lecture-tree routes (primarily `/api/v2/lectures`; bulk moves
// use v5). Every function here
// is a plain request that returns data and never touches the store (ora-fe
// ADR-0002). The Redux side lives in the slice thunks:
// `src/slices/lectures/lectureThunks.ts` (mutations),
// `src/slices/lectures/lectureLoadThunks.ts` (tree and preview loads),
// `src/slices/lectures/lectureContentThunks.ts` (videos, vignettes,
// flashcards) and `src/slices/lectures/lectureSelectionPersist.ts`.
import { isAxiosError } from 'axios';
import axiosInstance from './axiosCreator';
import { normalizeFlashcardsState } from '../utils/flashcardVariants';
import type { FlashcardsState } from '../types/Flashcard';
import { Video } from '../types/Video';
import type {
  LectureNodeType,
  LecturePreview,
  LectureTreeNode,
  LectureTreeResponse,
} from '../types/Lecture';
import { MergeStrategy } from '../slices/mappingJobsSlice/mappingJobsSlice';
import type {
  Vignette,
  VignetteAnswerOption,
  VignetteContent,
} from '../types/Vignette';

const normalizeVignetteAnswerOptions = (
  answerOptions: VignetteAnswerOption[] | undefined
): VignetteAnswerOption[] => {
  if (!Array.isArray(answerOptions)) return [];

  return answerOptions.map((answerOption, index) => ({
    ...answerOption,
    letter: answerOption.letter ?? answerOption.optionLetter ?? '',
    optionText: answerOption.optionText ?? '',
    isCorrect: Boolean(answerOption.isCorrect),
    explanationMain:
      answerOption.explanationMain ?? answerOption.explanation_main ?? '',
    explanationTutor:
      answerOption.explanationTutor ??
      answerOption.explanation_tutor_mode ??
      null,
    selectionCount:
      answerOption.selectionCount ?? answerOption.frequencyChosen ?? 0,
    ord: answerOption.ord ?? index,
  }));
};

const normalizeVignetteContent = (
  content: VignetteContent,
  index: number
): VignetteContent => ({
  ...content,
  question: content.question ?? '',
  answerOptions: normalizeVignetteAnswerOptions(content.answerOptions),
  correctAnswer: content.correctAnswer ?? '',
  explanation: content.explanation ?? '',
  learningObjective: content.learningObjective ?? '',
  references: Array.isArray(content.references) ? content.references : [],
  versionNum: content.versionNum ?? index,
  isQAed: Boolean(content.isQAed),
});

const normalizeLectureVignette = (vignette: Vignette): Vignette => {
  const normalizedContents = Array.isArray(vignette.contents)
    ? vignette.contents.map((content, index) =>
        normalizeVignetteContent(content, index)
      )
    : [];

  const normalizedPresentedVariant = vignette.presentedVariant
    ? normalizeVignetteContent(vignette.presentedVariant, 0)
    : undefined;

  if (vignette.schemaVersion !== 'v2') {
    return {
      ...vignette,
      contents: normalizedContents,
    };
  }

  const resolvedPresentedVariant =
    normalizedPresentedVariant ??
    (vignette.presentedVariantId
      ? normalizedContents.find(
          (content) => content.id === vignette.presentedVariantId
        )
      : undefined) ??
    normalizedContents[0];

  const resolvedContents =
    normalizedContents.length > 0
      ? normalizedContents
      : resolvedPresentedVariant
        ? [resolvedPresentedVariant]
        : [];

  return {
    ...vignette,
    contents: resolvedContents,
    presentedVariant: resolvedPresentedVariant ?? null,
    presentedVariantId:
      resolvedPresentedVariant?.id ?? vignette.presentedVariantId ?? null,
  };
};

export interface CreateLectureData {
  name?: string;
  description?: string;
  date?: string | null;
  examId: string;
  topicIds?: string[];
  vignetteIds?: string[];
  flashcardIds?: string[];
  videoIds?: string[];
  nodeType?: LectureNodeType;
  parentId?: string | null;
}

/**
 * Create a new lecture (or a lecture-tree folder, with `nodeType: 'FOLDER'`).
 * Lectures are custom-exam scoped content and must be created on a CUSTOM
 * exam id.
 */
export const postLecture = async ({
  name,
  description,
  date,
  examId,
  topicIds,
  vignetteIds,
  flashcardIds,
  videoIds,
  nodeType,
  parentId,
  // s3Key,
}: CreateLectureData): Promise<LecturePreview | LectureTreeNode> => {
  try {
    const response = await axiosInstance.post('/api/v2/lectures/', {
      name,
      description,
      date,
      examId,
      topicIds,
      vignetteIds,
      flashcardIds,
      videoIds,
      nodeType,
      parentId,
      // s3Key,
    });

    return response.data.data as LecturePreview | LectureTreeNode;
  } catch (error) {
    console.error('Error creating lecture:', error);
    throw error;
  }
};

export interface UpdateLectureData {
  name?: string;
  description?: string;
  date?: string | null;
  examId?: string;
  topicIds?: string[];
  vignetteIds?: string[];
  flashcardIds?: string[];
  videoIds?: string[];
  mergeStrategy?: MergeStrategy;
  ord?: number;
}

/**
 * Update an existing lecture. `PUT /:lectureId` answers with a
 * LectureTreeNode for a FOLDER and a LecturePreview for a DOCUMENT.
 */
export const putLecture = async (
  lectureId: string,
  {
    name,
    description,
    date,
    examId,
    topicIds,
    vignetteIds,
    flashcardIds,
    videoIds,
    // s3Key,
    mergeStrategy,
    ord,
  }: UpdateLectureData
): Promise<LecturePreview | LectureTreeNode | undefined> => {
  try {
    // Keep lecture exam ownership on CUSTOM exams (server enforces this too).
    const response = await axiosInstance.put(`/api/v2/lectures/${lectureId}`, {
      name,
      description,
      date,
      examId,
      topicIds,
      vignetteIds,
      flashcardIds,
      videoIds,
      // Deprecated s3Key
      // s3Key,
      mergeStrategy,
      ord,
    });

    return response.data.data;
  } catch (error) {
    console.error('Error updating lecture:', error);
    throw error;
  }
};

export interface DeleteLectureResult {
  deletedIds: string[];
}

/**
 * Delete a lecture or a folder. Resolves to every id the server removed (a
 * folder delete removes its subtree), or the requested id on the legacy empty
 * response.
 */
export const deleteLectureById = async (
  lectureId: string
): Promise<DeleteLectureResult> => {
  try {
    const response = await axiosInstance.delete(
      `/api/v2/lectures/${lectureId}`
    );

    const result = (response.data?.data ?? response.data) as
      Partial<DeleteLectureResult> | undefined;

    const deletedIds = Array.isArray(result?.deletedIds)
      ? result.deletedIds.filter((id): id is string => typeof id === 'string')
      : [lectureId];

    return { deletedIds: deletedIds.length > 0 ? deletedIds : [lectureId] };
  } catch (error) {
    console.error('Error deleting lecture:', error);
    throw error;
  }
};

export const putLectureStatus = async ({
  lectureId,
  enabled,
  hidden,
}: {
  lectureId: string;
  enabled?: boolean;
  hidden?: boolean;
}) => {
  const response = await axiosInstance.put(
    `/api/v2/lectures/${lectureId}/status`,
    { enabled, hidden }
  );

  return response.data.data;
};

export interface ViewableDocumentViewer {
  accessState: 'viewable';
  fileName: string;
  // Whether a viewer-ready PDF is available to stream (false when the source
  // couldn't be converted to a preview).
  hasPreview: boolean;
  conversionFailed?: boolean;
}

export interface LockedDocumentViewer {
  accessState: 'locked';
}

// The caller's own upload is still being mapped. The viewer shows a "still being
// processed" state and re-polls until it flips to viewable — see DocumentPdfViewer.
export interface ProcessingDocumentViewer {
  accessState: 'processing';
}

export type DocumentViewer =
  ViewableDocumentViewer | LockedDocumentViewer | ProcessingDocumentViewer;

// Resolve viewer metadata for a lecture's uploaded document. The backend renders
// a viewer-ready PDF on first open, so this request can take a moment for a
// non-PDF source (subsequent opens are served from cache). Returns NO URLs — the
// PDF bytes are fetched separately through fetchDocumentViewerFile so nothing but
// our own authenticated API is ever hit.
export const fetchDocumentViewer = async (
  lectureId: string
): Promise<DocumentViewer> => {
  const response = await axiosInstance.get(
    `/api/v2/lectures/${lectureId}/viewer`
  );

  return response.data.data;
};

export interface ContentSourceDocument {
  lectureId: string;
  fileName: string;
}

// All source PDFs a studied vignette/flashcard was mapped from that the current
// user may view (own uploads + verified shared-exam documents), one per lecture,
// own-first then newest (server-enforced). Backs the StudyPanel "Document" tab's
// per-document sub-tabs. Empty array when there is no viewable source.
export const fetchContentSourceDocuments = async (
  contentType: 'vignette' | 'flashcard',
  contentId: string,
  blockId?: string
): Promise<ContentSourceDocument[]> => {
  const response = await axiosInstance.get(
    `/api/v2/content/${contentType}/${contentId}/source-documents`,
    { params: blockId ? { blockId } : undefined }
  );

  return response.data?.data ?? [];
};

// Fetch the viewer-ready PDF bytes through our authenticated API (JWT attached
// by axiosInstance; the access guard is re-run server-side on every request), so
// react-pdf renders in-memory data with no presigned S3 URL exposed.
export const fetchDocumentViewerFile = async (
  lectureId: string
): Promise<ArrayBuffer> => {
  const response = await axiosInstance.get(
    `/api/v2/lectures/${lectureId}/viewer/file`,
    { responseType: 'arraybuffer' }
  );

  return response.data as ArrayBuffer;
};

export interface LectureTreeFetchResult {
  /** The tree, with every node stamped with the tree's exam id. */
  tree: LectureTreeResponse;
  /** Folders the member collapsed on an earlier visit (ORA-567), or null. */
  collapsedLectureFolderIds: string[] | null;
  hasCustomizedLectureSelection: boolean;
}

export const getLectureTree = async (
  examId: string
): Promise<LectureTreeFetchResult> => {
  const response = await axiosInstance.get('/api/v2/lectures/tree', {
    params: { examId },
  });

  const responseBody = response.data as
    | LectureTreeResponse
    | {
        data: LectureTreeResponse;
        meta?: LectureTreeResponse['meta'];
      };

  const payload = 'data' in responseBody ? responseBody.data : responseBody;

  const nodes = payload.nodes.map((node) => ({
    ...node,
    examId: payload.exam.id,
  }));

  const collapsedLectureFolderIds =
    payload.meta?.collapsedLectureFolderIds ??
    ('data' in responseBody
      ? responseBody.meta?.collapsedLectureFolderIds
      : undefined) ??
    null;

  // The tree endpoint reports selection customization alongside exam/nodes;
  // tolerate it on the response envelope too.
  const hasCustomizedLectureSelection = Boolean(
    payload.meta?.hasCustomizedLectureSelection ??
    ('data' in responseBody
      ? responseBody.meta?.hasCustomizedLectureSelection
      : undefined)
  );

  return {
    tree: { ...payload, nodes },
    collapsedLectureFolderIds,
    hasCustomizedLectureSelection,
  };
};

/**
 * Mirrors which lecture-tree folders the member has collapsed in an exam to
 * the server (ORA-567), so the layout follows them across devices. Best
 * effort: a school viewer without a membership row gets a 404 here and simply
 * keeps the default layout, and a transient failure leaves the previous set.
 */
export const updateLectureTreeCollapsedFolders = async ({
  examId,
  collapsedFolderIds,
}: {
  examId: string;
  collapsedFolderIds: string[];
}): Promise<void> => {
  try {
    await axiosInstance.put('/api/v2/lectures/tree/collapsed-folders', {
      examId,
      collapsedFolderIds,
    });
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return;
    console.error('Error saving lecture tree layout:', error);
  }
};

/** Bulk fetch lecture previews with counts, by exam or by ids. */
export const postBulkLecturePreviews = async ({
  examId,
  lectureIds,
  page,
  limit,
}: {
  examId?: string;
  lectureIds?: string[];
  page?: number;
  limit?: number;
}) => {
  const response = await axiosInstance.post('/api/v2/lectures/preview/bulk', {
    examId,
    lectureIds,
    page,
    limit,
  });

  return {
    lectures: response.data.data as LecturePreview[],
    hasCustomizedLectureSelection: Boolean(
      response.data.meta?.hasCustomizedLectureSelection
    ) as boolean,
  };
};

// Persist the current user's lecture selection for an exam (bulk-replace).
export const putLectureSelection = async (
  examId: string,
  selectedLectureIds: string[]
) => {
  await axiosInstance.put(`/api/v2/lectures/exam/${examId}/selection`, {
    selectedLectureIds,
  });
};

// Get Videos for a Specific Lecture
export const getLectureVideos = async (lectureId: string): Promise<Video[]> => {
  try {
    const response = await axiosInstance.get(
      `/api/v2/lectures/${lectureId}/videos`
    );

    return response.data.data;
  } catch (error) {
    console.error('Error fetching lecture videos:', error);
    throw error;
  }
};

/**
 * Options for the bulk lecture-content reads.
 *
 * The bulk endpoints only ever return UNSUSPENDED content, so a caller that
 * turns the returned list into a `REPLACE` (`set:`) write on the lecture would
 * silently unlink the document's suspended questions/flashcards (ORA-498 P5).
 * `includeSuspended` asks the backend for those hidden ids as a SEPARATE list
 * so the caller can union them back into the payload — the returned content
 * itself is byte-for-byte unchanged, and suspended items are never displayed.
 */
export interface LectureContentFetchOptions {
  includeSuspended?: boolean;
}

/** The ids the backend filtered out of `data` because they are suspended. */
export interface SuspendedContentIds {
  /** Always present (possibly empty) when `includeSuspended` was requested. */
  suspendedIds: string[];
}

const readSuspendedIds = (payload: unknown): string[] => {
  const suspendedIds = (
    payload as { suspendedIds?: unknown } | null | undefined
  )?.suspendedIds;

  return Array.isArray(suspendedIds)
    ? suspendedIds.filter((id): id is string => typeof id === 'string')
    : [];
};

/**
 * Fetch the vignettes of several lectures. `suspendedIds` is filled only when
 * `includeSuspended` is set.
 */
export const postLectureVignettes = async (
  lectureIds: string[],
  options?: LectureContentFetchOptions
): Promise<SuspendedContentIds & { vignettes: Vignette[] }> => {
  const response = await axiosInstance.post('/api/v2/lectures/vignettes/bulk', {
    lectureIds,
    ...(options?.includeSuspended ? { includeSuspended: true } : {}),
  });

  const vignettes: Vignette[] = (
    Array.isArray(response.data.data) ? response.data.data : []
  ).map((vignette: Vignette) => normalizeLectureVignette(vignette));

  return { vignettes, suspendedIds: readSuspendedIds(response.data) };
};

/**
 * Fetch the flashcards of several lectures. `suspendedIds` is filled only when
 * `includeSuspended` is set.
 */
export const postLectureFlashcards = async (
  lectureIds: string[],
  options?: LectureContentFetchOptions
): Promise<SuspendedContentIds & { flashcards: FlashcardsState }> => {
  const response = await axiosInstance.post(
    '/api/v2/lectures/flashcards/bulk',
    {
      lectureIds,
      ...(options?.includeSuspended ? { includeSuspended: true } : {}),
    }
  );

  const flashcards = normalizeFlashcardsState({
    new: response.data.data.new ?? [],
    due: response.data.data.due ?? [],
    completed: response.data.data.completed ?? [],
  });

  return { flashcards, suspendedIds: readSuspendedIds(response.data) };
};

/**
 * Fetch videos for multiple lectures in bulk
 * @param examId - Optional exam ID to fetch videos for all lectures in the exam
 * @param lectureIds - Optional array of specific lecture IDs to fetch videos for
 * @returns Promise<Record<string, Video[]>> - Object mapping lecture IDs to their video arrays
 */
export const postBulkLectureVideos = async ({
  examId,
  lectureIds,
  page,
  limit,
}: {
  examId?: string;
  lectureIds?: string[];
  page?: number;
  limit?: number;
}): Promise<Record<string, Video[]>> => {
  try {
    const response = await axiosInstance.post('/api/v2/lectures/videos/bulk', {
      examId,
      lectureIds,
      page,
      limit,
    });

    return response.data.data as Record<string, Video[]>;
  } catch (error) {
    console.error('Error fetching bulk lecture videos:', error);
    throw error;
  }
};

export const fetchLectureMappingJobs = async (lectureId: string) => {
  const response = await axiosInstance.get(
    `/api/v2/lectures/${lectureId}/mappingJobs`
  );

  return response.data.data;
};

/**
 * Reorder a lecture within an exam using dense order keys
 * One network request; returns new orderKey for the moved lecture
 */
export const postLectureReorder = async (
  lectureId: string,
  params: {
    examId: string;
    index?: number;
    beforeId?: string;
    afterId?: string;
  }
): Promise<string> => {
  const response = await axiosInstance.post(
    `/api/v2/lectures/${lectureId}/reorder`,
    params
  );

  const { orderKey } = response.data.data as {
    lectureId: string;
    orderKey: string;
  };

  return orderKey;
};

export interface MoveLectureParams {
  examId: string;
  parentId: string | null;
  beforeId?: string;
  afterId?: string;
  index?: number;
}

export interface MoveLectureResult {
  id: string;
  parentId: string | null;
  orderKey: string;
}

export interface MoveLecturesParams {
  examId: string;
  lectureIds: string[];
  parentId: string | null;
  beforeId?: string;
  afterId?: string;
}

export interface MoveLecturesResult {
  moved: MoveLectureResult[];
}

export const postLectureMove = async (
  lectureId: string,
  params: MoveLectureParams
): Promise<MoveLectureResult> => {
  const response = await axiosInstance.post(
    `/api/v2/lectures/${lectureId}/move`,
    params
  );

  return (response.data?.data ?? response.data) as MoveLectureResult;
};

export const postLectureMoveMany = async (
  params: MoveLecturesParams
): Promise<MoveLecturesResult> => {
  const response = await axiosInstance.post('/api/v5/lectures/move', params);

  return (response.data?.data ?? response.data) as MoveLecturesResult;
};
